import { Input } from '../ui';
import { PhoneInputUK, PostcodeInput, Select, looksLikeUKPhone, looksLikeUKPostcode } from '../forms';
import { type TerritorySource } from '../../lib/auth-onboarding';
import type { ConsentAudience } from '../../lib/consents';

/**
 * Candidate-only disclosure shown under the optional mobile field (frame C-2).
 * Supplying a number implies WhatsApp/mobile consent for job application
 * updates — and explicitly NOT for marketing.
 */
export const WHATSAPP_NOTICE =
  'By sharing your phone number, you agree to being contacted via WhatsApp and mobile about job application updates. Your number will not be used for marketing purposes.';

export interface BaseProfileState {
  territory: string;
  territorySource: TerritorySource;
  phone: string;
  postcode: string;
  county: string;
  // Employer-only required terms tick-box. Candidates imply acceptance by
  // pressing "Create account", so this stays false and unused for them.
  acceptedTerms: boolean;
  // OPT-OUT: true = the user does NOT want marketing email. Collected on the
  // terms step, but held here so the parent pages can carry it through the
  // wizard and into the completion request. Inverted in lib/consents.ts.
  marketingOptOut: boolean;
}

export const EMPTY_BASE_PROFILE: BaseProfileState = {
  territory: 'GB',
  territorySource: 'default',
  phone: '',
  postcode: '',
  county: '',
  acceptedTerms: false,
  marketingOptOut: false,
};

export type BaseProfileErrors = Partial<Record<keyof BaseProfileState, string>>;

// Advisory only — pkg/phone + pkg/ukpostcode on the server are authoritative.
// Terms acceptance is gated on the dedicated terms step, not here.
export function validateBaseProfile(state: BaseProfileState): BaseProfileErrors {
  const errs: BaseProfileErrors = {};
  if (state.phone.trim() && !looksLikeUKPhone(state.phone)) errs.phone = 'Enter a valid UK phone number.';
  if (!looksLikeUKPostcode(state.postcode)) errs.postcode = 'Enter a valid UK postcode (e.g. SW1A 1AA).';
  return errs;
}

export interface BaseProfileFieldsProps {
  value: BaseProfileState;
  errors: BaseProfileErrors;
  /** Drives the candidate-only WhatsApp disclosure. Defaults to candidate. */
  audience?: ConsentAudience;
  disabled?: boolean;
  onChange: <K extends keyof BaseProfileState>(key: K, next: BaseProfileState[K]) => void;
}

export function BaseProfileFields({
  value,
  errors,
  audience = 'candidate',
  disabled,
  onChange,
}: BaseProfileFieldsProps): JSX.Element {
  return (
    <>
      <Select
        label="Country"
        value={value.territory}
        disabled={disabled}
        {...(errors.territory ? { errorText: errors.territory } : {})}
        {...(value.territorySource === 'geoip'
          ? { helpText: 'Detected from your location — adjust if wrong.' }
          : {})}
        options={[{ value: 'GB', label: 'United Kingdom' }]}
        onChange={(e) => {
          onChange('territory', e.target.value);
          onChange('territorySource', 'user_selected');
        }}
      />

      <div className="flex flex-col gap-1">
        <PhoneInputUK
          label="Mobile number (optional)"
          value={value.phone}
          disabled={disabled}
          error={errors.phone}
          onChange={(e) => onChange('phone', e.target.value)}
        />
        {/*
          Employers capture no WhatsApp/mobile consent, so the disclosure — and
          the consent it implies — is candidate-only.
        */}
        {audience === 'candidate' ? (
          <p className="text-sm text-text-secondary">{WHATSAPP_NOTICE}</p>
        ) : null}
      </div>

      <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <PostcodeInput
          label="Postcode"
          value={value.postcode}
          disabled={disabled}
          {...(errors.postcode ? { errorText: errors.postcode } : {})}
          onChange={(e) => onChange('postcode', e.target.value)}
        />
        <Input
          label="County (optional)"
          value={value.county}
          disabled={disabled}
          {...(errors.county ? { errorText: errors.county } : {})}
          onChange={(e) => onChange('county', e.target.value)}
        />
      </div>
    </>
  );
}
